import type { AppliedBy } from "@unbox/api";
import { Plus } from "@untitledui/icons";
import { useMemo } from "react";
import { useCreatorCohorts } from "@/api/creator-cohorts";
import { Table } from "@/components/application/table/table";
import { RuleSignalIcon } from "@/components/rules/rule-signal-icon";
import { RuleSourceTooltip } from "@/components/rules/rule-source-tooltip";
import { CreatorTag } from "@/pages/creator-tags/tag";
import { CohortPickerPopover } from "./cohort-picker-popover";

type CreatorCohortCellProps = {
  creatorId: string;
  /** The creator's cohort id (name/colour come from the live cohort list), or null. */
  cohortId: string | null;
  appliedBy?: AppliedBy | null;
  /** Opens the Cohort Library modal (owned by the page) from the "Manage All Cohorts" link. */
  onManageAllCohorts: () => void;
};

// Bare content, no `<Table.Cell>` wrapper — for callers (like UnboxTable columns)
// that supply their own cell wrapper.
export const CreatorCohortCellContent = ({
  creatorId,
  cohortId,
  appliedBy,
  onManageAllCohorts,
}: CreatorCohortCellProps) => {
  const { data: allCohorts = [], isLoading } = useCreatorCohorts();

  // Resolve the assigned id to a live cohort so name/colour edits in the library
  // reflect here without refetching the row.
  const cohort = useMemo(
    () => (cohortId ? allCohorts.find((c) => c.id === cohortId) : undefined),
    [allCohorts, cohortId],
  );

  // While the cohort list loads we can't resolve the name/colour yet — show a
  // placeholder for creators that have an assignment rather than flashing an
  // empty "+ Add Cohort" cell. Unassigned creators need no wait.
  if (isLoading && cohortId) {
    return <span className="block h-5 w-20 animate-pulse rounded-full bg-secondary" aria-hidden />;
  }

  const isEmpty = !cohort;

  return (
    // The whole cell is the picker trigger so it's hard to mis-click onto the
    // creator row. Must be a real <button>: react-aria-components excludes
    // native interactive elements from the row's onRowAction (a div[role=button]
    // would still open the creator drawer). stopPropagation guards selection.
    <CohortPickerPopover creatorId={creatorId} assignedCohortId={cohortId} onManageAllCohorts={onManageAllCohorts}>
      <button
        type="button"
        aria-label={isEmpty ? "Add cohort" : "Edit cohort"}
        onClick={(e) => e.stopPropagation()}
        className="flex min-h-7 w-full cursor-pointer flex-row flex-wrap items-center gap-1 rounded-md px-1 py-0.5 text-left transition-colors hover:bg-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
      >
        {isEmpty ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-dashed border-secondary px-2.5 py-0.5 text-xs font-medium text-tertiary">
            <Plus className="size-3.5" />
            Add Cohort
          </span>
        ) : (
          <RuleSourceTooltip appliedBy={appliedBy} action="added">
            <span className="inline-flex">
              <CreatorTag
                tag={cohort}
                showTooltip={!appliedBy}
                iconTrailing={appliedBy?.source === "rule" ? <RuleSignalIcon /> : undefined}
              />
            </span>
          </RuleSourceTooltip>
        )}
      </button>
    </CohortPickerPopover>
  );
};

export const CreatorCohortCell = (props: CreatorCohortCellProps) => (
  <Table.Cell className="px-2">
    <CreatorCohortCellContent {...props} />
  </Table.Cell>
);
